import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Clock } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const NAVY  = '#071B3A';
const GOLD  = '#C99316';
const IVORY = '#FAF8F3';

// 3 curated articles for the homepage Insights section
const INSIGHT_SLUGS = [
  'respect-across-cultures',
  'common-sense-local-knowledge',
  'belonging-without-disappearing',
];

const InsightsSection = () => {
  const [articles, setArticles] = useState([]);

  useEffect(() => {
    Promise.all(
      INSIGHT_SLUGS.map(slug =>
        fetch(`${API_URL}/api/public/articles/${slug}`)
          .then(r => (r.ok ? r.json() : null))
          .catch(() => null)
      )
    ).then(results => setArticles(results.filter(Boolean)));
  }, []);

  if (articles.length === 0) return null;

  return (
    <section
      className="py-20 md:py-28"
      style={{ backgroundColor: IVORY }}
      data-testid="insights-section"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">

        {/* Section header */}
        <div className="flex items-end justify-between mb-12">
          <div>
            <span
              className="block text-[11px] tracking-[0.3em] uppercase font-sans font-medium mb-2"
              style={{ color: GOLD + 'B3' }}
            >
              From the Author
            </span>
            <h2
              className="font-serif font-light tracking-tight leading-[1.1]"
              style={{ fontSize: 'clamp(1.6rem, 2.8vw, 2.2rem)', color: NAVY }}
            >
              Insights for Leading Across Cultures
            </h2>
          </div>
          <Link
            to="/articles"
            className="hidden md:inline-flex items-center gap-1.5 font-sans text-[11px] uppercase tracking-[0.2em] transition-all duration-300 hover:gap-3 pb-px"
            style={{ color: NAVY + '40', borderBottom: `1px solid ${NAVY}15` }}
            onMouseEnter={e => { e.currentTarget.style.color = GOLD; e.currentTarget.style.borderBottomColor = GOLD + '40'; }}
            onMouseLeave={e => { e.currentTarget.style.color = NAVY + '40'; e.currentTarget.style.borderBottomColor = NAVY + '15'; }}
            data-testid="insights-all-articles-link"
          >
            All Articles <ArrowRight size={11} />
          </Link>
        </div>

        {/* Article cards grid */}
        <div className="grid md:grid-cols-3 gap-0" style={{ border: `1px solid ${NAVY}0A` }}>
          {articles.map((article, i) => (
            <Link
              key={article.slug}
              to={`/articles/${article.slug}`}
              className="group flex flex-col"
              style={{
                borderRight: i < articles.length - 1 ? `1px solid ${NAVY}0A` : 'none',
                backgroundColor: 'white',
              }}
              data-testid={`insight-card-${article.slug}`}
            >
              {/* Thumbnail */}
              {article.featured_image && (
                <div className="relative overflow-hidden" style={{ height: '200px' }}>
                  <img
                    src={article.featured_image}
                    alt={article.title}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
                  />
                  <div
                    className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                    style={{ backgroundColor: `${NAVY}0A` }}
                  />
                </div>
              )}

              {/* Content */}
              <div className="flex flex-col flex-1 p-7 md:p-8">
                {/* Category + reading time */}
                <div className="flex items-center gap-3 mb-4">
                  {article.categories?.[0] && (
                    <span
                      className="font-sans text-[9px] uppercase tracking-[0.22em] px-2 py-0.5"
                      style={{ backgroundColor: GOLD + '10', color: GOLD }}
                    >
                      {article.categories[0]}
                    </span>
                  )}
                  {article.reading_time && (
                    <span
                      className="flex items-center gap-1 font-sans text-[10px]"
                      style={{ color: NAVY + '30' }}
                    >
                      <Clock size={9} /> {article.reading_time} min read
                    </span>
                  )}
                </div>

                {/* Title */}
                <h3
                  className="font-serif font-light tracking-tight leading-[1.2] mb-3 flex-1 transition-colors duration-300"
                  style={{ fontSize: '1.125rem', color: NAVY }}
                >
                  {article.title}
                </h3>

                {/* Excerpt */}
                {article.excerpt && (
                  <p
                    className="font-sans font-light leading-[1.75] mb-5"
                    style={{ fontSize: '0.875rem', color: NAVY + '50', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
                  >
                    {article.excerpt}
                  </p>
                )}

                {/* CTA */}
                <span
                  className="inline-flex items-center gap-2 font-sans text-[10px] uppercase tracking-[0.2em] font-medium transition-all duration-300 group-hover:gap-3"
                  style={{ color: GOLD }}
                >
                  Read Article <ArrowRight size={11} />
                </span>
              </div>
            </Link>
          ))}
        </div>

        {/* Mobile: All Articles link */}
        <div className="flex justify-center mt-8 md:hidden">
          <Link
            to="/articles"
            className="inline-flex items-center gap-1.5 font-sans text-[11px] uppercase tracking-[0.2em]"
            style={{ color: NAVY + '40' }}
          >
            All Articles <ArrowRight size={11} />
          </Link>
        </div>

      </div>
    </section>
  );
};

export default InsightsSection;
